import type { UsersRepository } from './users.repository'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type { RedisClient } from '../../core/redis/client.js'
import type {
  UserLocationSummary,
  UserLocationUpdateDTO,
  UserStatsSnapshot,
  UserStatsAggregated,
  UserStatsInsight
} from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { safeJsonParse } from '../../utils/sanitize.js'

const logger = createLogger('UsersService')

export class UsersService {
  private readonly cachePrefix = 'user:stats:'
  private readonly cacheTTL = 3600 // 1 hour
  private readonly locationCachePrefix = 'user:location:summary:'
  private readonly locationCacheTTL = 30 // short-lived cache for live tracking
  private readonly cacheEnabled = process.env.ENABLE_REDIS_CACHE !== 'false'
  private readonly statsPromiseByUser = new Map<string, Promise<UserStatsSnapshot>>()

  constructor(
    private readonly usersRepository: UsersRepository,
    private readonly redis: RedisClient
  ) {}

  async calculateUserStats(userId: string): Promise<UserStatsSnapshot> {
    logger.debug('Resolving user stats', { userId })
    if (this.cacheEnabled) {
      const cachedStats = await this.getCachedStats(userId)
      if (cachedStats) {
        logger.debug('User stats cache hit', { userId })
        return cachedStats
      }
      logger.debug('User stats cache miss', { userId })
    }

    const runningComputation = this.statsPromiseByUser.get(userId)
    if (runningComputation) {
      logger.debug('Stats computation already running', { userId })
      return await runningComputation
    }

    const [storedSnapshot, planSignature] = await Promise.all([
      this.usersRepository.getStoredStatsSnapshot(userId),
      this.usersRepository.getPlanSignature(userId)
    ])

    if (storedSnapshot.snapshot && storedSnapshot.signature === planSignature.signature) {
      logger.debug('Stats snapshot fresh, returning persisted payload', { userId })
      if (this.cacheEnabled) {
        await this.cacheStats(userId, storedSnapshot.snapshot)
      }
      return storedSnapshot.snapshot
    }

    if (!storedSnapshot.snapshot) {
      return await this.computeAndCacheStats(userId)
    }

    // Snapshot exists but is stale; return it immediately while refreshing async.
    this.scheduleStatsRefresh(userId)
    if (this.cacheEnabled) {
      await this.cacheStats(userId, storedSnapshot.snapshot)
    }
    return storedSnapshot.snapshot
  }

  private async getCachedStats(userId: string): Promise<UserStatsSnapshot | null> {
    const cacheKey = `${this.cachePrefix}${userId}`
    const cached = await this.redis.get(cacheKey)
    
    if (!cached) {
      return null
    }

    try {
      // Validate that cached value is a valid JSON string
      if (isString(cached)) {
        const parsed = safeJsonParse<UserStatsSnapshot>(cached)
        if (parsed && parsed.tripsCreated !== undefined) {
          return parsed
        }
      } else {
        logger.warn('Cached value is not a string', { userId, type: typeof cached })
      }
    } catch (error) {
      logger.warn('Failed to parse cached stats', { userId, error: (error as Error).message })
    }

    // Invalid cache - delete it
    await this.redis.del(cacheKey)
    return null
  }

  private async cacheStats(userId: string, stats: UserStatsSnapshot): Promise<void> {
    const cacheKey = `${this.cachePrefix}${userId}`
    try {
      // Ensure all values are properly serializable
      const serialized = JSON.stringify(stats)
      
      // Validate that serialization worked correctly
      if (serialized && serialized !== '[object Object]' && !serialized.includes('[object Object]')) {
        await this.redis.setex(cacheKey, this.cacheTTL, serialized)
        logger.debug('User stats cached', { userId, ttl: this.cacheTTL })
      } else {
        logger.warn('Invalid serialization result', { userId })
      }
    } catch (error) {
      logger.warn('Failed to cache user stats', { userId, error: (error as Error).message })
      // Don't throw - caching is not critical
    }
  }

  async invalidateUserStatsCache(userId: string): Promise<void> {
    logger.debug('Invalidating user stats cache', { userId })
    if (this.cacheEnabled) {
      await this.redis.del(`${this.cachePrefix}${userId}`)
    }
  }

  private createEmptySummary(): UserLocationSummary {
    return {
      totalDistanceMeters: 0,
      sessionDistanceMeters: 0,
      trackedDurationMs: 0,
      averageSpeedKmh: 0,
      samples: 0
    }
  }

  async updateUserLocation(
    userId: string,
    payload: UserLocationUpdateDTO
  ): Promise<UserLocationSummary> {
    const sanitized = this.sanitizeLocationPayload(payload)
    const summary = await this.usersRepository.saveUserLocation(userId, sanitized)

    if (this.cacheEnabled) {
      await this.cacheLocationSummary(userId, summary)
    }

    return summary
  }

  async getUserLocationSummary(userId: string): Promise<UserLocationSummary> {
    if (this.cacheEnabled) {
      const cachedSummary = await this.getCachedLocationSummary(userId)
      if (cachedSummary) {
        return cachedSummary
      }
    }

    const summary = await this.usersRepository.getLocationSummary(userId)
    const normalized = summary ?? this.createEmptySummary()

    if (this.cacheEnabled && summary) {
      await this.cacheLocationSummary(userId, normalized)
    }

    return normalized
  }

  private sanitizeLocationPayload(payload: UserLocationUpdateDTO): UserLocationUpdateDTO {
    const latitude = Number(payload.latitude)
    const longitude = Number(payload.longitude)

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      throw new Error('Invalid latitude value')
    }

    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      throw new Error('Invalid longitude value')
    }

    const accuracy =
      payload.accuracy === undefined ? undefined : Number(payload.accuracy)
    const altitude = payload.altitude === undefined ? undefined : Number(payload.altitude)
    const speed = payload.speed === undefined ? undefined : Number(payload.speed)
    const heading = payload.heading === undefined ? undefined : Number(payload.heading)

    let timestamp: string | undefined
    if (payload.timestamp) {
      const parsedTimestamp = new Date(payload.timestamp)
      if (Number.isNaN(parsedTimestamp.getTime())) {
        throw new TypeError('Invalid timestamp value')
      }
      timestamp = parsedTimestamp.toISOString()
    }

    return {
      latitude,
      longitude,
      accuracy: Number.isFinite(accuracy) ? accuracy : undefined,
      altitude: Number.isFinite(altitude) ? altitude : undefined,
      speed: Number.isFinite(speed) ? speed : undefined,
      heading: Number.isFinite(heading) ? heading : undefined,
      timestamp,
      source: payload.source
    }
  }

  private buildInsights(stats: UserStatsAggregated): UserStatsInsight[] {
    const insights: UserStatsInsight[] = []

    this.addTripCreationInsights(insights, stats)
    this.addLastTripInsights(insights, stats)
    this.addFavoriteCountryInsight(insights, stats)
    this.addDistanceMilestoneInsight(insights, stats)

    return insights.slice(0, 4)
  }

  private addTripCreationInsights(insights: UserStatsInsight[], stats: UserStatsAggregated): void {
    if (stats.tripsCreated === 0) {
      insights.push({
        key: 'first-trip',
        title: 'Lancez votre premier roadtrip',
        description: 'Créez un plan pour débloquer vos premières statistiques et recommandations personnalisées.',
        icon: 'rocket_launch',
        tone: 'info'
      })
    } else if (stats.tripsCreated >= 5) {
      insights.push({
        key: 'power-user',
        title: 'Vous êtes un planificateur aguerri',
        description: `${stats.tripsCreated} voyages créés. Pensez à partager vos itinéraires avec vos copilotes !`,
        icon: 'military_tech',
        tone: 'positive'
      })
    } else {
      insights.push({
        key: 'next-trip',
        title: 'Continuez sur votre lancée',
        description: `Vous avez déjà planifié ${stats.tripsCreated} voyage${stats.tripsCreated > 1 ? 's' : ''}. Ajoutez-en un nouveau pour enrichir vos stats.`,
        icon: 'trending_up',
        tone: 'info'
      })
    }
  }

  private addLastTripInsights(insights: UserStatsInsight[], stats: UserStatsAggregated): void {
    if (stats.lastTripAt) {
      const lastTrip = new Date(stats.lastTripAt)
      const daysSince = this.diffInDays(lastTrip, new Date())
      
      if (!Number.isFinite(daysSince)) {
        return
      }

      if (daysSince <= 14) {
        insights.push({
          key: 'fresh-trip',
          title: 'Dernière aventure toute récente',
          description: `Votre dernier roadtrip date d'il y a ${Math.max(0, Math.round(daysSince))} jour${daysSince > 1 ? 's' : ''}.`,
          icon: 'schedule',
          tone: 'positive'
        })
      } else if (daysSince > 45) {
        insights.push({
          key: 'reactivate',
          title: 'Il est temps de repartir',
          description: "Vous n'avez pas planifié depuis plus de 45 jours. Programmez une nouvelle escapade pour maintenir votre rythme.",
          icon: 'directions_car',
          tone: 'warning'
        })
      }
    } else {
      insights.push({
        key: 'no-trip-yet',
        title: 'Aucun voyage enregistré',
        description: 'Planifiez un itinéraire pour obtenir des recommandations adaptées et suivre vos progrès.',
        icon: 'map',
        tone: 'info'
      })
    }
  }

  private addFavoriteCountryInsight(insights: UserStatsInsight[], stats: UserStatsAggregated): void {
    const favoriteCountry = stats.topCountries[0]
    if (favoriteCountry) {
      insights.push({
        key: 'favorite-country',
        title: `Cap sur ${favoriteCountry.country}`,
        description: `Vous avez exploré ${favoriteCountry.country} à ${favoriteCountry.count} reprise${favoriteCountry.count > 1 ? 's' : ''}. Découvrez de nouveaux spots à proximité !`,
        icon: 'public',
        tone: 'positive'
      })
    }
  }

  private addDistanceMilestoneInsight(insights: UserStatsInsight[], stats: UserStatsAggregated): void {
    if (stats.kmDriven >= 1000) {
      insights.push({
        key: 'distance-milestone',
        title: 'Kilomètres au compteur',
        description: `Vous avez déjà parcouru ${stats.kmDriven} km planifiés avec RoadTripper. Pensez à activer le suivi en temps réel pour des statistiques encore plus précises.`,
        icon: 'route',
        tone: 'positive'
      })
    }
  }

  private diffInDays(from: Date, to: Date): number {
    const diff = to.getTime() - from.getTime()
    return diff / (1000 * 60 * 60 * 24)
  }

  private locationCacheKey(userId: string): string {
    return `${this.locationCachePrefix}${userId}`
  }

  private async getCachedLocationSummary(userId: string): Promise<UserLocationSummary | null> {
    const cacheKey = this.locationCacheKey(userId)
    const cached = await this.redis.get(cacheKey)
    if (!cached) {
      return null
    }

    const parsed = safeJsonParse<UserLocationSummary>(cached)
    if (!parsed) {
      await this.redis.del(cacheKey)
      return null
    }

    return parsed
  }

  private async cacheLocationSummary(userId: string, summary: UserLocationSummary): Promise<void> {
    const cacheKey = this.locationCacheKey(userId)
    try {
      await this.redis.setex(cacheKey, this.locationCacheTTL, JSON.stringify(summary))
    } catch (error) {
      logger.warn('Failed to cache location summary', { userId, error: (error as Error).message })
    }
  }

  private async buildUserStats(userId: string): Promise<UserStatsSnapshot> {
    // Expensive computation
    logger.info('Computing user stats', { userId })
    const { stats, plans, signature } = await this.usersRepository.getUserStats(userId)
    const snapshot: UserStatsSnapshot = {
      ...stats,
      insights: this.buildInsights(stats)
    }
    await this.usersRepository.persistAggregatedStats(userId, stats, plans, signature, snapshot)
    logger.info('User stats computed', { userId, trips: stats.tripsCreated, totalKm: Math.round(stats.kmDriven) })
    return snapshot
  }

  private async computeAndCacheStats(userId: string): Promise<UserStatsSnapshot> {
    const computation = this.buildUserStats(userId)
    this.statsPromiseByUser.set(userId, computation)
    try {
      const snapshot = await computation
      if (this.cacheEnabled) {
        await this.cacheStats(userId, snapshot)
      }
      return snapshot
    } finally {
      this.statsPromiseByUser.delete(userId)
    }
  }

  private scheduleStatsRefresh(userId: string): void {
    if (this.statsPromiseByUser.has(userId)) {
      return
    }
    const computation = this.buildUserStats(userId)
    this.statsPromiseByUser.set(userId, computation)
    ;(async () => {
      try {
        const snapshot = await computation
        if (this.cacheEnabled) {
          await this.cacheStats(userId, snapshot)
        }
      } catch (error) {
        logger.warn('Background stats recompute failed', { userId, error: (error as Error).message })
      }
    })()
      .finally(() => {
        this.statsPromiseByUser.delete(userId)
      })
  }
}
